// ============================================
// QuickFiltersCard Component
// Department filter chips card
// ============================================

import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

export interface DepartmentFilter {
  key: string;
  label: string;
  count: number;
}

interface QuickFiltersCardProps {
  filters: DepartmentFilter[];
  activeKey?: string | null;
  onFilter: (key: string | null) => void;
  isLoading?: boolean;
  className?: string;
}

// ============================================
// Component
// ============================================

export function QuickFiltersCard({
  filters,
  activeKey,
  onFilter,
  isLoading,
  className,
}: QuickFiltersCardProps) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className={cn('h-36 rounded-xl bg-[#F3F3FE] p-6', className)}>
        <div className="h-3 w-24 animate-pulse rounded bg-slate-300" />
        <div className="mt-4 flex flex-wrap gap-2">
          <div className="h-8 w-28 animate-pulse rounded-lg bg-slate-300" />
          <div className="h-8 w-24 animate-pulse rounded-lg bg-slate-300" />
          <div className="h-8 w-20 animate-pulse rounded-lg bg-slate-300" />
        </div>
      </div>
    );
  }

  const handleChipClick = (key: string) => {
    if (activeKey === key) {
      onFilter(null);
    } else {
      onFilter(key);
    }
  };

  return (
    <div className={cn('h-36 rounded-xl bg-[#F3F3FE] p-6', className)}>
      <p className="text-xs font-bold uppercase tracking-[1.2px] text-[#434655]">
        {t('quickFilters') || 'Quick Filters'}
      </p>

      {/* Department Chips */}
      <div className="mt-4 flex flex-wrap gap-2">
        {filters.map((filter) => (
          <button
            key={filter.key}
            onClick={() => handleChipClick(filter.key)}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
              activeKey === filter.key
                ? 'bg-primary-600 text-white'
                : 'bg-[#E1E2ED] text-[#191B23] hover:bg-[#D1D2DD]'
            )}
          >
            {filter.label} ({filter.count})
          </button>
        ))}
      </div>
    </div>
  );
}
